import { getSalesBudgetYearlyInput } from 'api/storeManagement';
import { useEffect, useState } from 'react';
import moment from 'moment';
import { updateSalesBudgetYear, getFiscalYearFromDateToYear, updateSalesBudgetYearlyInput } from 'api/salesBudgetYear';
import useToastNotification from 'hooks/useToastNotification';
import StoreManagementDomain from 'domain/master/storeManagement/salesBudgetYearInput';
import { useFormik } from 'formik';
import { AnalyticalClassificationType, BudgetType } from 'domain/master/storeManagement/salesBudgetDailyInput';
import {
  calcBreakEventPointCustomer,
  calcBreakEventPointSale,
  calcContributionMargin,
  calcContributionMarginRate,
  calcEmployeeAndPartCostRate,
  calcFLACost,
  calcFLACostRate,
  calcFLAROCost,
  calcFLAROCostRate,
  calcGrossProfit,
  calcLaborRate,
  calcMarginalProfit,
  calcMarginalProfitRate,
  calcNumberOfCustomers,
  calcOperatingIncome,
  calcOperatingIncomeRate,
  calcOtherCostRate,
  calcProfitLossCost,
  calcProfitLossCostRate,
  calcTurnoverRate,
  getYearlyTotalCostList,
} from 'components/organismos/master/storeManagement/salesBudgetYear/util';
import { SalesBudgetMonthType } from 'components/organismos/master/storeManagement/salesBudgetYear/SalesBudgetDailyInputLayout/type';
import { SaleBudgetYearResponseType } from './type';

type ErrorType = {
  defaultMessage: string;
};

type StartEndTime = {
  start: Date;
  end: Date;
};

export const useSalesBudgetYear = (year: number, orgCode: string) => {
  const { successNotification, errorNotification } = useToastNotification();
  const [categoryAry, setCategoryAry] = useState<Array<string>>([]);
  const [canRegist, setCanRegist] = useState<boolean>(false); // 登録フラグのstate
  const [salesBudget, setSalesBudget] = useState<SalesBudgetMonthType>({
    listOfOperatingIncome: [],
    listOfRateOperatingIncome: [],
    listNumberOfCustomers: [],
    listOfTurnoverRate: [],
    listOfMarginalProfit: [],
    listRateOfMarginalProfit: [],
    listOfRateContributionMargin: [],
    listOfBreakEventPointSale: [],
    listOfBreakEventPointNumber: [],
    listOfLaborShare: [],
    listOfFLACost: [],
    listOfFLACostRate: [],
    listOfProfitLossCost: [],
    listOfProfitLossCostRate: [],
    listOfFLAROCost: [],
    listOfFLAROCostRate: [],
    listOfContributionMargin: [],
    listSumOfOtherTotalCostRate: [],
    listSumOfAdvertisingSaleAndSaleBudgetFree: [],
    listSumOfFoodCostAndDrinkCost: [],
    listSumOfAdvertisingExpenseEachDay: [],
    listSumOfEmployeeAndPartCostRatio: [],
    listSumOfOtherTotalCost: [],
    listSumOfRentEachDay: [],
    listSumOfEmployeeLaborCostAndPartLaborCost: [],
    sumOfAdvertisingSaleAndSumOfSaleBudgetFreeRow: 0,
    sumOfTotalEmployeeLaborCostAndPartLaborCost: 0,
    sumOfAdvertisingExpense: 0,
    sumOfLandRentRow: 0,
    sumOfFoodCostAndDrinkCost: 0,
    sumOfNumberOfCustomers: 0,
    sumOfTurnoverRate: 0,
    sumOfMarginalProfit: 0,
    sumOfRateMarginalProfit: 0,
    sumOfContributionMargin: 0,
    sumOfRateContributionMargin: 0,
    sumOfBreakEventPointSale: 0,
    sumOfBreakEventPointNumber: 0,
    sumOfSumOfEmployeeAndPartCostRatio: 0,
    sumOfLaborShare: 0,
    sumOfFLACostRate: 0,
    sumOfProfitLossCost: 0,
    sumOfProfitLossCostRate: 0,
    sumOfFLAROCost: 0,
    sumOfFLACost: 0,
    sumOfOtherTotalCost: 0,
    sumOfOtherTotalCostRate: 0,
    totalOfOperatingIncome: 0,
    rateOfOperationIncome: 0,
  });

  const [getIsLoading, setIsLoading] = useState(true);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false); // 確認モーダルの状態管理

  const onInputChange = (category: string, index: number, field: string, value: number) => {
    const categoryArray = (formik.values as any)[category] || [];
    const updatedArray = [...categoryArray];
    if (updatedArray[index]) {
      updatedArray[index] = {
        ...updatedArray[index],
        [field]: value,
      };
    }
    formik.setFieldValue(category, updatedArray);

    // 値変更後に計算を再実行
    setTimeout(() => {
      const currentValues = formik.values as any;
      const yearLtSeat = currentValues.yearLtSeat || [];
      calcTable(yearLtSeat);
    }, 0);
  };

  const onSubmit = async () => {
    setConfirmModalOpen(false);
    setIsLoading(true);

    try {
      const values = formik.values;

      const budgetInputDataList = [
        ...(values.yearLtSales ?? []),
        ...(values.yearLtCost ?? []),
        ...(values.yearLtLabor ?? []),
        ...(values.yearLtAdvertising ?? []),
        ...(values.yearLtRent ?? []),
        ...(values.yearLtExpenses ?? []),
        ...(values.yearLtOther ?? []),
        ...(values.yearLtCustomPrice ? [values.yearLtCustomPrice] : []),
      ]?.map((data) => ({
        analyticalItemCode: data.analyticalItemCode,
        analyticalItemName: data.analyticalItemName,
        analyticalItemId: data.analyticalItemId,
        advertisingMediaMstCode: data.advertisingMediaMstCode,
        analyticalClassificationType: data.analyticalClassificationType,
        expensesClassification: data.expensesClassification || 0,
        budgetType: data.budgetType,
        sumFlag: data.sumFlag,
        monthlyAmountList: data.monthAry,
        budgetDistributionType: data.budgetDiscributionType,
      }));

      const budgetSummaryDataList: {
        analyticalClassificationType: AnalyticalClassificationType;
        totalAmount: number;
      }[] = [
        {
          analyticalClassificationType: AnalyticalClassificationType.S_SALES,
          totalAmount: salesBudget.sumOfAdvertisingSaleAndSumOfSaleBudgetFreeRow,
        },
        {
          analyticalClassificationType: AnalyticalClassificationType.F_FOOD_COST,
          totalAmount: salesBudget.sumOfFoodCostAndDrinkCost,
        },
        {
          analyticalClassificationType: AnalyticalClassificationType.L_LABOR_COST,
          totalAmount: salesBudget.sumOfTotalEmployeeLaborCostAndPartLaborCost,
        },
        {
          analyticalClassificationType: AnalyticalClassificationType.A_ADVERTISING_COST,
          totalAmount: salesBudget.sumOfAdvertisingExpense,
        },
        {
          analyticalClassificationType: AnalyticalClassificationType.R_RENT,
          totalAmount: salesBudget.sumOfLandRentRow,
        },
        {
          analyticalClassificationType: AnalyticalClassificationType.O_OTHER_EXPENSES,
          totalAmount: salesBudget.sumOfOtherTotalCost,
        },
      ];

      const orgCode = sessionStorage.getItem('orgCode_DestinationOrg') || '';
      const companyCode = sessionStorage.getItem('loginUser.companyCode') || '';
      const targetYear = year.toString(); // yearパラメータを使用
      const updateUser = sessionStorage.getItem('loginUser.staffName') || '';
      const loginStaffCode = sessionStorage.getItem('loginUser.staffCode') || '';

      await updateSalesBudgetYearlyInput(companyCode, {
        orgCode,
        targetYear: `${targetYear}-01-01`,
        loginStaffCode,
        updateUser,
        budgetInputDataList,
        budgetSummaryDataList,
      });

      successNotification('登録しました。');
    } catch (error) {
      if (error.response?.data?.data?.errors) {
        const listErr = error.response.data.data.errors;
        let stringErr = '';
        listErr.forEach((element: ErrorType) => {
          stringErr += `${element.defaultMessage}<br />`;
        });
        errorNotification(stringErr);
      } else {
        errorNotification('サーバー側でエラーが発生しました。');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const openConfirmModal = () => {
    setConfirmModalOpen(true);
  };

  const closeConfirmModal = () => {
    setConfirmModalOpen(false);
  };

  const formik = useFormik({
    initialValues: StoreManagementDomain.generateInitial(),
    onSubmit: openConfirmModal,
  });

  useEffect(() => {
    setIsLoading(true);
    getSalesBudgetYearlyInput({
      orgCode: orgCode || sessionStorage.getItem('orgCode') || 'all',
      targetYear: `${year}/01/01`,
      categoryAry,
    })
      .then((response: SaleBudgetYearResponseType) => {
        formik.setFieldValue('yearLtMonth', response.yearLtMonth);
        formik.setFieldValue('yearLtSales', response.yearLtSales);
        formik.setFieldValue('yearLtCost', response.yearLtCost);
        formik.setFieldValue('yearLtLabor', response.yearLtLabor);
        formik.setFieldValue('yearLtAdvertising', response.yearLtAdvertising);
        formik.setFieldValue('yearLtRent', response.yearLtRent);
        formik.setFieldValue('yearLtExpenses', response.yearLtExpenses);
        formik.setFieldValue('yearLtOther', response.yearLtOther);
        formik.setFieldValue('yearLtCustomPrice', response.yearLtCustomPrice);

        // 登録フラグを設定
        setCanRegist(response.canRegist || false);

        calcTable(response.yearLtSeat);
      })
      .finally(() => {
        setIsLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, orgCode, categoryAry]);

  const calcTable = (yearLtSeat: number[]) => {
    const {
      yearLtMonth,
      yearLtSales,
      yearLtCost,
      yearLtLabor,
      yearLtAdvertising,
      yearLtRent,
      yearLtExpenses,
      yearLtCustomPrice,
    } = formik.values;

    let listCalcAdvertisingSaleAndSaleBudgetFree = Array.from({ length: yearLtMonth.length }, () => ({
      totalCost: 0,
      totalVariableCost: 0,
      totalFixedCost: 0,
    }));

    if (yearLtSales !== null) {
      listCalcAdvertisingSaleAndSaleBudgetFree = getYearlyTotalCostList(yearLtSales, yearLtMonth);
    }

    let listSumOfFoodCostAndDrinkCost = Array.from({ length: yearLtMonth.length }, () => ({
      totalCost: 0,
      totalVariableCost: 0,
      totalFixedCost: 0,
    }));

    if (yearLtCost !== null) {
      listSumOfFoodCostAndDrinkCost = getYearlyTotalCostList(yearLtCost, yearLtMonth);
    }

    let listSumOfEmployeeLaborCostAndPartLaborCost = Array.from({ length: yearLtMonth.length }, () => ({
      totalCost: 0,
      totalVariableCost: 0,
      totalFixedCost: 0,
    }));

    if (yearLtLabor !== null) {
      listSumOfEmployeeLaborCostAndPartLaborCost = getYearlyTotalCostList(yearLtLabor, yearLtMonth);
    }

    let listSumOfAdvertisingExpenseEachDay = Array.from({ length: yearLtMonth.length }, () => ({
      totalCost: 0,
      totalVariableCost: 0,
      totalFixedCost: 0,
    }));

    if (yearLtAdvertising !== null) {
      listSumOfAdvertisingExpenseEachDay = getYearlyTotalCostList(yearLtAdvertising, yearLtMonth);
    }

    let listSumOfRentEachDay = Array.from({ length: yearLtMonth.length }, () => ({
      totalCost: 0,
      totalVariableCost: 0,
      totalFixedCost: 0,
    }));

    if (yearLtRent !== null) {
      listSumOfRentEachDay = getYearlyTotalCostList(yearLtRent, yearLtMonth);
    }

    let listSumOfOtherTotalCost = Array.from({ length: yearLtMonth.length }, () => ({
      totalCost: 0,
      totalVariableCost: 0,
      totalFixedCost: 0,
    }));

    if (yearLtExpenses !== null) {
      listSumOfOtherTotalCost = getYearlyTotalCostList(yearLtExpenses, yearLtMonth);
    }

    const listSumOfFixedCost: number[] = Array.from({ length: yearLtMonth.length }, () => 0);

    const listSumOfVariableCost: number[] = Array.from({ length: yearLtMonth.length }, () => 0);

    // 固定費と変動費の合計を計算
    yearLtMonth.forEach((_, i) => {
      listSumOfFixedCost[i] =
        listSumOfEmployeeLaborCostAndPartLaborCost[i].totalFixedCost +
        listSumOfFoodCostAndDrinkCost[i].totalFixedCost +
        listSumOfAdvertisingExpenseEachDay[i].totalFixedCost +
        listSumOfRentEachDay[i].totalFixedCost +
        listSumOfOtherTotalCost[i].totalFixedCost;

      listSumOfVariableCost[i] =
        listSumOfEmployeeLaborCostAndPartLaborCost[i].totalVariableCost +
        listSumOfFoodCostAndDrinkCost[i].totalVariableCost +
        listSumOfAdvertisingExpenseEachDay[i].totalVariableCost +
        listSumOfRentEachDay[i].totalVariableCost +
        listSumOfOtherTotalCost[i].totalVariableCost;
    });

    // 初期化
    const listSumOfOtherTotalCostRate = Array.from({ length: yearLtMonth.length }, () => 0);
    const listNumberOfCustomers = Array.from({ length: yearLtMonth.length }, () => 0);
    const listGrossProfit = Array.from({ length: yearLtMonth.length }, () => 0);
    const listOfTurnoverRate = Array.from({ length: yearLtMonth.length }, () => 0);
    const listOfMarginalProfit = Array.from({ length: yearLtMonth.length }, () => 0);
    const listRateOfMarginalProfit = Array.from({ length: yearLtMonth.length }, () => 0);
    const listOfContributionMargin = Array.from({ length: yearLtMonth.length }, () => 0);
    const listOfRateContributionMargin = Array.from({ length: yearLtMonth.length }, () => 0);
    const listOfBreakEventPointSale = Array.from({ length: yearLtMonth.length }, () => 0);
    const listOfBreakEventPointNumber = Array.from({ length: yearLtMonth.length }, () => 0);
    const listSumOfEmployeeAndPartCostRatio = Array.from({ length: yearLtMonth.length }, () => 0);
    const listOfLaborShare = Array.from({ length: yearLtMonth.length }, () => 0);
    const listOfFLACost = Array.from({ length: yearLtMonth.length }, () => 0);
    const listOfFLACostRate = Array.from({ length: yearLtMonth.length }, () => 0);
    const listOfProfitLossCost = Array.from({ length: yearLtMonth.length }, () => 0);
    const listOfProfitLossCostRate = Array.from({ length: yearLtMonth.length }, () => 0);
    const listOfFLAROCost = Array.from({ length: yearLtMonth.length }, () => 0);
    const listOfFLAROCostRate = Array.from({ length: yearLtMonth.length }, () => 0);
    const listOfOperatingIncome = Array.from({ length: yearLtMonth.length }, () => 0);
    const listOfRateOperatingIncome = Array.from({ length: yearLtMonth.length }, () => 0);

    let numberOfSeat = 0;

    yearLtMonth.forEach((data, i) => {
      if (numberOfSeat === 0 && yearLtSeat[i] > 0) {
        numberOfSeat = yearLtSeat[i];
      }

      const perCustomerPrice = yearLtCustomPrice?.monthAry[i] ?? 0;

      // その他経費率
      listSumOfOtherTotalCostRate[i] = calcOtherCostRate(
        listCalcAdvertisingSaleAndSaleBudgetFree[i].totalCost,
        listSumOfOtherTotalCost[i].totalCost,
      );

      // 客数
      listNumberOfCustomers[i] = calcNumberOfCustomers(
        perCustomerPrice,
        listCalcAdvertisingSaleAndSaleBudgetFree[i].totalCost,
      );

      // 粗利益
      listGrossProfit[i] = calcGrossProfit(
        listCalcAdvertisingSaleAndSaleBudgetFree[i].totalCost,
        listSumOfFoodCostAndDrinkCost[i].totalCost,
      );

      // 回転率
      listOfTurnoverRate[i] = calcTurnoverRate(numberOfSeat, listNumberOfCustomers[i]);

      // 限界利益額
      listOfMarginalProfit[i] = calcMarginalProfit(
        listCalcAdvertisingSaleAndSaleBudgetFree[i].totalCost,
        listSumOfVariableCost[i],
      );

      // 限界利益率
      listRateOfMarginalProfit[i] = calcMarginalProfitRate(
        listCalcAdvertisingSaleAndSaleBudgetFree[i].totalCost,
        listOfMarginalProfit[i],
      );

      // 貢献利益額
      listOfContributionMargin[i] = calcContributionMargin(listOfMarginalProfit[i], listSumOfFixedCost[i]);

      // 貢献利益率
      listOfRateContributionMargin[i] = calcContributionMarginRate(
        listCalcAdvertisingSaleAndSaleBudgetFree[i].totalCost,
        listOfContributionMargin[i],
      );

      // 損益分岐点 売上
      listOfBreakEventPointSale[i] = calcBreakEventPointSale(listRateOfMarginalProfit[i], listSumOfFixedCost[i]);

      // 損益分岐点 客数
      listOfBreakEventPointNumber[i] = calcBreakEventPointCustomer(perCustomerPrice, listOfBreakEventPointSale[i]);

      // 人件費率 合計
      listSumOfEmployeeAndPartCostRatio[i] = calcEmployeeAndPartCostRate(
        listCalcAdvertisingSaleAndSaleBudgetFree[i].totalCost,
        listSumOfEmployeeLaborCostAndPartLaborCost[i].totalCost,
      );

      // 人件費率
      listOfLaborShare[i] = calcLaborRate(listGrossProfit[i], listSumOfEmployeeLaborCostAndPartLaborCost[i].totalCost);

      // FLA合計
      listOfFLACost[i] = calcFLACost(
        listSumOfFoodCostAndDrinkCost[i].totalCost,
        listSumOfEmployeeLaborCostAndPartLaborCost[i].totalCost,
        listSumOfAdvertisingExpenseEachDay[i].totalCost,
      );

      // FLA率
      listOfFLACostRate[i] = calcFLACostRate(listCalcAdvertisingSaleAndSaleBudgetFree[i].totalCost, listOfFLACost[i]);

      // 店舗損益額
      listOfProfitLossCost[i] = calcProfitLossCost(
        listCalcAdvertisingSaleAndSaleBudgetFree[i].totalCost,
        listOfFLACost[i],
      );

      // 店舗損益率
      listOfProfitLossCostRate[i] = calcProfitLossCostRate(
        listCalcAdvertisingSaleAndSaleBudgetFree[i].totalCost,
        listOfProfitLossCost[i],
      );

      // FLARO合計
      listOfFLAROCost[i] = calcFLAROCost(
        listOfFLACost[i],
        listSumOfRentEachDay[i].totalCost,
        listSumOfOtherTotalCost[i].totalCost,
      );

      // FLARO率
      listOfFLAROCostRate[i] = calcFLAROCostRate(
        listCalcAdvertisingSaleAndSaleBudgetFree[i].totalCost,
        listOfFLAROCost[i],
      );

      // 営業利益
      listOfOperatingIncome[i] = calcOperatingIncome(
        listCalcAdvertisingSaleAndSaleBudgetFree[i].totalCost,
        listOfFLAROCost[i],
      );

      // 営業利益率
      listOfRateOperatingIncome[i] = calcOperatingIncomeRate(
        listCalcAdvertisingSaleAndSaleBudgetFree[i].totalCost,
        listOfOperatingIncome[i],
      );
    });

    // 合計値を計算
    const sumOfContributionMargin = listOfContributionMargin.reduce((acc, val) => acc + val, 0);
    const sumOfBreakEventPointSale = listOfBreakEventPointSale.reduce((acc, val) => acc + val, 0);
    const sumOfBreakEventPointNumber = listOfBreakEventPointNumber.reduce((acc, val) => acc + val, 0);
    const sumOfFLAROCost = listOfFLAROCost.reduce((acc, val) => acc + val, 0);
    const sumOfFLACost = listOfFLACost.reduce((acc, val) => acc + val, 0);
    const sumOfProfitLossCost = listOfProfitLossCost.reduce((acc, val) => acc + val, 0);
    const sumOfOtherTotalCost = listSumOfOtherTotalCost.reduce((acc, val) => acc + val.totalCost, 0);
    const sumOfFoodCostAndDrinkCost = listSumOfFoodCostAndDrinkCost.reduce((acc, val) => acc + val.totalCost, 0);
    const sumOfLandRentRow = listSumOfRentEachDay.reduce((acc, val) => acc + val.totalCost, 0);
    const sumOfAdvertisingExpense = listSumOfAdvertisingExpenseEachDay.reduce((acc, val) => acc + val.totalCost, 0);
    const sumOfTotalEmployeeLaborCostAndPartLaborCost = listSumOfEmployeeLaborCostAndPartLaborCost.reduce(
      (acc, val) => acc + val.totalCost,
      0,
    );
    const sumOfAdvertisingSaleAndSumOfSaleBudgetFreeRow = listCalcAdvertisingSaleAndSaleBudgetFree.reduce(
      (acc, val) => acc + val.totalCost,
      0,
    );
    const sumOfNumberOfCustomers = listNumberOfCustomers.reduce((acc, val) => acc + val, 0);
    const sumOfMarginalProfit = listOfMarginalProfit.reduce((acc, val) => acc + val, 0);
    const sumOfGrossProfit = listGrossProfit.reduce((acc, val) => acc + val, 0);

    const sumOfRateMarginalProfit = calcMarginalProfitRate(
      sumOfAdvertisingSaleAndSumOfSaleBudgetFreeRow,
      sumOfMarginalProfit,
    );
    const sumOfTurnoverRate = numberOfSeat > 0 ? sumOfNumberOfCustomers / numberOfSeat / 100 : 0;
    const sumOfOtherTotalCostRate = calcOtherCostRate(
      sumOfAdvertisingSaleAndSumOfSaleBudgetFreeRow,
      sumOfOtherTotalCost,
    );
    const sumOfFLACostRate = calcFLACostRate(sumOfAdvertisingSaleAndSumOfSaleBudgetFreeRow, sumOfFLACost);
    const sumOfProfitLossCostRate = sumOfAdvertisingSaleAndSumOfSaleBudgetFreeRow
      ? sumOfProfitLossCost / sumOfAdvertisingSaleAndSumOfSaleBudgetFreeRow
      : 0;
    const sumOfRateContributionMargin = calcContributionMarginRate(
      sumOfAdvertisingSaleAndSumOfSaleBudgetFreeRow,
      sumOfContributionMargin,
    );
    const sumOfSumOfEmployeeAndPartCostRatio = calcEmployeeAndPartCostRate(
      sumOfAdvertisingSaleAndSumOfSaleBudgetFreeRow,
      sumOfTotalEmployeeLaborCostAndPartLaborCost,
    );
    const sumOfLaborShare = calcLaborRate(sumOfGrossProfit, sumOfTotalEmployeeLaborCostAndPartLaborCost);

    const totalOfOperatingIncome = sumOfAdvertisingSaleAndSumOfSaleBudgetFreeRow - sumOfFLAROCost;
    const rateOfOperationIncome = sumOfAdvertisingSaleAndSumOfSaleBudgetFreeRow
      ? totalOfOperatingIncome / sumOfAdvertisingSaleAndSumOfSaleBudgetFreeRow
      : 0;

    setSalesBudget({
      listOfOperatingIncome,
      listOfRateOperatingIncome,
      listNumberOfCustomers,
      listOfTurnoverRate,
      listOfMarginalProfit,
      listRateOfMarginalProfit,
      listOfContributionMargin,
      listOfRateContributionMargin,
      listOfBreakEventPointSale,
      listOfBreakEventPointNumber,
      listOfLaborShare,
      listOfFLACost,
      listOfFLACostRate,
      listOfProfitLossCost,
      listOfProfitLossCostRate,
      listOfFLAROCost,
      listOfFLAROCostRate,
      listSumOfAdvertisingSaleAndSaleBudgetFree: listCalcAdvertisingSaleAndSaleBudgetFree.map((item) => item.totalCost),
      listSumOfOtherTotalCost: listSumOfOtherTotalCost.map((item) => item.totalCost),
      listSumOfEmployeeLaborCostAndPartLaborCost: listSumOfEmployeeLaborCostAndPartLaborCost.map(
        (item) => item.totalCost,
      ),
      listSumOfFoodCostAndDrinkCost: listSumOfFoodCostAndDrinkCost.map((item) => item.totalCost),
      listSumOfAdvertisingExpenseEachDay: listSumOfAdvertisingExpenseEachDay.map((item) => item.totalCost),
      listSumOfRentEachDay: listSumOfRentEachDay.map((item) => item.totalCost),
      listSumOfOtherTotalCostRate,
      listSumOfEmployeeAndPartCostRatio,
      sumOfAdvertisingSaleAndSumOfSaleBudgetFreeRow,
      sumOfTotalEmployeeLaborCostAndPartLaborCost,
      sumOfAdvertisingExpense,
      sumOfLandRentRow,
      sumOfFoodCostAndDrinkCost,
      sumOfNumberOfCustomers,
      sumOfTurnoverRate,
      sumOfMarginalProfit,
      sumOfRateMarginalProfit,
      sumOfContributionMargin,
      sumOfRateContributionMargin,
      sumOfBreakEventPointSale,
      sumOfBreakEventPointNumber,
      sumOfSumOfEmployeeAndPartCostRatio,
      sumOfLaborShare,
      sumOfFLACostRate,
      sumOfFLAROCost,
      sumOfFLACost,
      sumOfProfitLossCost,
      sumOfProfitLossCostRate,
      sumOfOtherTotalCost,
      sumOfOtherTotalCostRate,
      totalOfOperatingIncome,
      rateOfOperationIncome,
    });
  };

  return {
    formik,
    getRows: salesBudget,
    getIsLoading,
    setIsLoading,
    categoryAry,
    setCategoryAry,
    onSubmit,
    onInputChange,
    canRegist, // 登録フラグを返り値に追加
    confirmModalOpen,
    closeConfirmModal,
  };
};

export const useGetFiscalYear2 = (dateSelect: number) => {
  const Start = sessionStorage.getItem('fiscalStartEnd.Start') || new Date();
  const End = sessionStorage.getItem('fiscalStartEnd.End') || new Date();

  const [fiscalStartEnd, setfiscalStartEnd] = useState<StartEndTime>({
    start: moment(Start).toDate(),
    end: moment(End).toDate(),
  });
  useEffect(() => {
    getFiscalYearFromDateToYear(dateSelect).then((response) => {
      const from = response.fiscalStartMonth;
      const to = response.fiscalEndMonth;
      const dateObject = { start: from, end: to };
      setfiscalStartEnd(dateObject);
    });
  }, [dateSelect]);
  return { fiscalStartEnd };
};

export const downloadOriginalFile = async (response: any, downloadFileName: string) => {
  const file = new Blob([response], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  saveAs(file, downloadFileName);
};

export const downloadCSVFile = async (response: any, downloadFileName: string) => {
  const file = new Blob([response], {
    type: 'text/csv',
  });
  saveAs(file, downloadFileName);
};

export const createFileName = (
  orgCode: string,
  prefix: string,
  dateSelect: string | null | number,
  extension: '.zip' | '.xlsx' | '.csv',
) => {
  const procDateStr = moment(new Date()).format('YYYYMMDDHHmmss');

  const fileName = `${
    orgCode === 'all'
      ? '全店舗'
      : orgCode
          .trim()
          .replace(/\\/g, '￥')
          .replace(/\//g, '／')
          .replace(/:/g, '：')
          .replace(/\*/g, '＊')
          .replace(/\?/g, '？')
          .replace(/\/"/g, '”')
          .replace(/</g, '＜')
          .replace(/>/g, '＞')
          .replace(/\|/g, '｜')
  }_${prefix}${dateSelect}_${procDateStr}${extension}`;
  return fileName;
};

export default useSalesBudgetYear;
